import { emptySceneV2 } from "./scene/migrateV1ToV2.js";
export const BOARD_TEMPLATE_IDS = [
    "empty",
    "lesson_scheme",
    "problem_task",
    "mind_map",
    "kanban",
    "flowchart",
    "lesson_feedback",
    "english_vocabulary",
];
/** Список для UI кабинета (порядок = отображение). */
export const BOARD_TEMPLATES = [
    {
        id: "empty",
        title: "Пустая",
        description: "Чистый холст — начинаете с нуля",
    },
    {
        id: "lesson_scheme",
        title: "Схема урока",
        description: "Тема, цели, ход, практика, домашнее задание",
    },
    {
        id: "problem_task",
        title: "Задача",
        description: "Условие, решение и ответ — как лист тетради",
    },
    {
        id: "mind_map",
        title: "Интеллект-карта",
        description: "Тема в центре и ветки вокруг",
    },
    {
        id: "kanban",
        title: "Канбан",
        description: "Сделать → В процессе → Готово",
    },
    {
        id: "flowchart",
        title: "Блок-схема",
        description: "Алгоритм: начало, ввод, условие, действие, конец",
    },
    {
        id: "lesson_feedback",
        title: "Обратная связь",
        description: "Понял · Сложно · Вопросы — в конце урока",
    },
    {
        id: "english_vocabulary",
        title: "Слова",
        description: "Английский: слово и перевод в двух колонках",
    },
];
/** Шаблоны, которые больше не предлагаем в меню виджетов. */
const HIDDEN_WIDGET_TEMPLATE_IDS = new Set([
    "lesson_scheme",
    "problem_task",
    "mind_map",
    "kanban",
    "flowchart",
    "lesson_feedback",
]);
/** Шаблоны для вставки на доску (без «Пустая» и снятых виджетов). */
export const BOARD_WIDGET_TEMPLATES = BOARD_TEMPLATES.filter((tpl) => tpl.id !== "empty" && !HIDDEN_WIDGET_TEMPLATE_IDS.has(tpl.id));
export function isBoardTemplateId(value) {
    return typeof value === "string" && BOARD_TEMPLATE_IDS.includes(value);
}
function tid(n) {
    return `a1000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
}
function lineObj(n, x1, y1, x2, y2, style, zIndex = "0", locked = true) {
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const w = Math.max(Math.abs(x2 - x1), 1);
    const h = Math.max(Math.abs(y2 - y1), 1);
    return {
        id: tid(n),
        type: "line",
        x,
        y,
        w,
        h,
        points: [
            [x1, y1],
            [x2, y2],
        ],
        locked,
        zIndex,
        style,
    };
}
function arrowObj(n, x1, y1, x2, y2, zIndex = "5", locked = true) {
    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const w = Math.max(Math.abs(x2 - x1), 1);
    const h = Math.max(Math.abs(y2 - y1), 1);
    return {
        id: tid(n),
        type: "arrow",
        x,
        y,
        w,
        h,
        points: [
            [x1, y1],
            [x2, y2],
        ],
        locked,
        zIndex,
        style: {
            stroke: "#64748b",
            strokeWidth: 2,
            strokeLinecap: "round",
            markerEnd: "triangle",
        },
    };
}
function rectObj(n, x, y, w, h, style, zIndex = "0", locked = true) {
    return {
        id: tid(n),
        type: "rect",
        x,
        y,
        w,
        h,
        locked,
        zIndex,
        cornerRadius: 12,
        style,
    };
}
function diamondObj(n, x, y, w, h, zIndex = "8", locked = true) {
    return {
        id: tid(n),
        type: "diamond",
        x,
        y,
        w,
        h,
        locked,
        zIndex,
        style: {
            stroke: "#6366f1",
            strokeWidth: 2,
            fill: "#eef2ff",
        },
    };
}
function stickyObj(n, x, y, w, h, text, backgroundColor, zIndex = "10") {
    return {
        id: tid(n),
        type: "sticky",
        x,
        y,
        w,
        h,
        text,
        zIndex,
        style: { backgroundColor, color: "#1e293b", fontSize: 16 },
    };
}
/** Подпись секции (не стикер — не мешает редактированию). */
function labelObj(n, x, y, w, text, fontSize = 15, color = "#475569") {
    return textObj(n, x, y, w, 28, text, fontSize, "25", true, color);
}
/** Большая редактируемая заметка; опционально заголовок внутри. */
function noteObj(n, x, y, w, h, backgroundColor, header = "", zIndex = "11") {
    const body = header ? `${header}\n\n` : "";
    return stickyObj(n, x, y, w, h, body, backgroundColor, zIndex);
}
function textObj(n, x, y, w, h, text, fontSize, zIndex = "20", locked = true, color = "#334155") {
    return {
        id: tid(n),
        type: "text",
        x,
        y,
        w,
        h,
        text,
        locked,
        zIndex,
        style: { color, fontSize, fontFamily: "system-ui, sans-serif" },
    };
}
function buildLessonSchemeScene() {
    const objects = [
        noteObj(1, -240, -340, 480, 120, "#fef08a", "Тема урока"),
        noteObj(2, -480, -190, 300, 300, "#bbf7d0", "Цели"),
        noteObj(3, -150, -190, 300, 300, "#bfdbfe", "Ход урока"),
        noteObj(4, 180, -190, 300, 300, "#fbcfe8", "Практика"),
        noteObj(5, -240, 140, 480, 160, "#e9d5ff", "Домашнее задание"),
    ];
    return { version: 2, objects };
}
function buildProblemTaskScene() {
    const objects = [
        textObj(100, -380, -400, 200, 36, "Задача", 22, "30", true, "#0f172a"),
        noteObj(101, -380, -340, 760, 170, "#fef9c3", "Условие"),
        noteObj(102, -380, -150, 760, 320, "#ffffff", "Решение"),
        noteObj(103, -180, 200, 360, 130, "#dcfce7", "Ответ"),
    ];
    return { version: 2, objects };
}
function buildMindMapScene() {
    const cx = 0;
    const cy = 0;
    const centerRect = { x: cx - 130, y: cy - 80, w: 260, h: 160 };
    const branches = [
        [-320, -280, 220, 150, "Подтема 1", "#fef08a"],
        [100, -280, 220, 150, "Подтема 2", "#bbf7d0"],
        [-380, 70, 220, 150, "Подтема 3", "#bfdbfe"],
        [160, 70, 220, 150, "Подтема 4", "#fbcfe8"],
        [-240, 240, 220, 150, "Подтема 5", "#e9d5ff"],
        [20, 240, 220, 150, "Подтема 6", "#fed7aa"],
    ];
    const objects = [
        textObj(201, -200, -420, 400, 32, "Интеллект-карта", 20, "30"),
        noteObj(200, centerRect.x, centerRect.y, centerRect.w, centerRect.h, "#fde68a", "Тема урока", "12"),
    ];
    let n = 210;
    const lineStyle = { stroke: "#94a3b8", strokeWidth: 2, strokeOpacity: 0.8, strokeLinecap: "round" };
    const attachPointToRect = (rect, toX, toY) => {
        const cx0 = rect.x + rect.w / 2;
        const cy0 = rect.y + rect.h / 2;
        const dx = toX - cx0;
        const dy = toY - cy0;
        const eps = 1e-6;
        if (Math.abs(dx) < eps && Math.abs(dy) < eps)
            return [Math.round(cx0), Math.round(cy0)];
        const sx = rect.w / 2 / Math.max(Math.abs(dx), eps);
        const sy = rect.h / 2 / Math.max(Math.abs(dy), eps);
        const t = Math.min(sx, sy);
        return [Math.round(cx0 + dx * t), Math.round(cy0 + dy * t)];
    };
    for (const [bx, by, bw, bh, title, color] of branches) {
        const branchRect = { x: bx, y: by, w: bw, h: bh };
        const tx = bx + bw / 2;
        const ty = by + bh / 2;
        const [x1, y1] = attachPointToRect(centerRect, tx, ty);
        const [x2, y2] = attachPointToRect(branchRect, cx, cy);
        objects.push(lineObj(n++, x1, y1, x2, y2, lineStyle));
        objects.push(noteObj(n++, bx, by, bw, bh, color, title));
    }
    return { version: 2, objects };
}
function buildKanbanScene() {
    const colW = 280;
    const colH = 520;
    const gap = 20;
    const startX = -((colW * 3 + gap * 2) / 2);
    const cols = [
        ["Сделать", "#f8fafc"],
        ["В процессе", "#eff6ff"],
        ["Готово", "#f0fdf4"],
    ];
    const objects = [textObj(300, -220, -420, 400, 32, "Канбан", 20, "30")];
    let n = 310;
    cols.forEach(([title, bg], i) => {
        const x = startX + i * (colW + gap);
        objects.push(rectObj(n++, x, -360, colW, colH, {
            stroke: "#cbd5e1",
            strokeWidth: 1,
            fill: bg,
            fillOpacity: 0.95,
        }));
        objects.push(labelObj(n++, x + 16, -348, colW - 32, title, 16, "#0f172a"));
        objects.push(noteObj(n++, x + 16, -300, colW - 32, colH - 56, "#ffffff"));
    });
    return { version: 2, objects };
}
function buildFlowchartScene() {
    const objects = [
        textObj(400, -120, -420, 240, 32, "Блок-схема", 20, "30"),
        labelObj(401, -80, -360, 160, "Начало", 14, "#166534"),
        noteObj(402, -110, -330, 220, 64, "#bbf7d0"),
        arrowObj(403, 0, -266, 0, -242),
        noteObj(404, -140, -230, 280, 100, "#ffffff", "Ввод данных"),
        arrowObj(405, 0, -130, 0, -108),
        diamondObj(406, -100, -100, 200, 110),
        textObj(407, -72, -78, 144, 44, "Условие?", 16, "12"),
        arrowObj(408, 0, 10, 0, 34),
        noteObj(409, -140, 40, 280, 100, "#bfdbfe", "Действие"),
        arrowObj(410, 0, 140, 0, 164),
        labelObj(411, -72, 178, 144, "Конец", 14, "#9f1239"),
        noteObj(412, -110, 200, 220, 64, "#fecdd3"),
    ];
    return { version: 2, objects };
}
function buildLessonFeedbackScene() {
    const colW = 280;
    const colH = 440;
    const gap = 20;
    const startX = -((colW * 3 + gap * 2) / 2);
    const cols = [
        ["Понял", "#f0fdf4"],
        ["Сложно", "#fff7ed"],
        ["Вопросы", "#eff6ff"],
    ];
    const objects = [
        textObj(500, -240, -420, 440, 32, "Обратная связь", 20, "30"),
        textObj(501, -300, -380, 600, 28, "Двойной клик по стикеру — писать · углы — растянуть", 13, "29", true, "#64748b"),
    ];
    let n = 510;
    cols.forEach(([title, bg], i) => {
        const x = startX + i * (colW + gap);
        objects.push(rectObj(n++, x, -320, colW, colH, {
            stroke: "#cbd5e1",
            strokeWidth: 1,
            fill: bg,
            fillOpacity: 0.95,
        }));
        objects.push(labelObj(n++, x + 16, -308, colW - 32, title, 16, "#0f172a"));
        objects.push(noteObj(n++, x + 16, -260, colW - 32, colH - 56, "#ffffff"));
    });
    return { version: 2, objects };
}
/** Две колонки: английское слово и перевод (по 8 пар стикеров). */
function buildEnglishVocabularyScene() {
    const tableW = 800;
    const tableH = 640;
    const tableX = -tableW / 2;
    const tableY = -320;
    const colW = 360;
    const colGap = 40;
    const leftColX = tableX + 16;
    const rightColX = leftColX + colW + colGap;
    const rowH = 56;
    const rowGap = 12;
    const rows = 8;
    const headerY = tableY + 20;
    const firstRowY = tableY + 68;
    const objects = [
        textObj(600, tableX, tableY - 48, tableW, 36, "Слова и переводы", 20, "30"),
        rectObj(601, tableX, tableY, tableW, tableH, {
            stroke: "#cbd5e1",
            strokeWidth: 1,
            fill: "#f8fafc",
            fillOpacity: 0.95,
        }),
        lineObj(602, 0, tableY + 52, 0, tableY + tableH - 16, { stroke: "#e2e8f0", strokeWidth: 1, strokeDash: "dashed" }),
        labelObj(603, leftColX, headerY, colW, "Слово (EN)", 15, "#1e40af"),
        labelObj(604, rightColX, headerY, colW, "Перевод", 15, "#166534"),
    ];
    let n = 610;
    for (let i = 0; i < rows; i++) {
        const y = firstRowY + i * (rowH + rowGap);
        objects.push(stickyObj(n++, leftColX, y, colW, rowH, "", "#dbeafe"));
        objects.push(stickyObj(n++, rightColX, y, colW, rowH, "", "#dcfce7"));
    }
    return { version: 2, objects };
}
export function sceneForBoardTemplate(id) {
    switch (id) {
        case "empty":
            return emptySceneV2();
        case "lesson_scheme":
            return buildLessonSchemeScene();
        case "problem_task":
            return buildProblemTaskScene();
        case "mind_map":
            return buildMindMapScene();
        case "kanban":
            return buildKanbanScene();
        case "flowchart":
            return buildFlowchartScene();
        case "lesson_feedback":
            return buildLessonFeedbackScene();
        case "english_vocabulary":
            return buildEnglishVocabularyScene();
        default: {
            const _exhaustive = id;
            return _exhaustive;
        }
    }
}
